import {
	Badge,
	Box,
	Button,
	Center,
	Collapse,
	Flex,
	Icon,
	IconButton,
	InputGroup,
	InputLeftElement,
	Spinner,
	Text,
	useColorModeValue
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import { Input } from '@chakra-ui/react';
import { format } from 'date-fns';
import { TbDatabaseSearch } from 'react-icons/tb';
import { MdSearch, MdErrorOutline, MdSearchOff, MdHistory, MdChevronRight, MdExpandLess, MdExpandMore } from 'react-icons/md';
import Card from '../../components/card/Card';
import ResultCard from './ResultCard';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { RootState } from '../../store/store';
import { consultarCpf, fetchConsultaHistory, openHistoryLead } from '../../store/features/consulta/consulta.slice';
import { QueryStatus } from '../../models/consulta.model';
import { toastService } from '../../services/toast.service';

function maskCpf(cpf: string) {
	const d = (cpf || '').replace(/\D/g, '').padStart(11, '0');
	return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9, 11)}`;
}

export default function Consulta() {
	const dispatch = useAppDispatch();
	const [cpf, setCpf] = useState('');
	const [historyOpen, setHistoryOpen] = useState(true);
	const { result, loading, searched, history } = useAppSelector((state: RootState) => state.consulta);
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
	const rowHover = useColorModeValue('secondaryGray.200', 'navy.900');

	const cleanCpf = cpf.replace(/\D/g, '');
	// CPF com menos de 11 dígitos (zeros à esquerda perdidos) é completado com zeros.
	const paddedCpf = cleanCpf.length > 0 && cleanCpf.length <= 11 ? cleanCpf.padStart(11, '0') : cleanCpf;
	const canSearch = paddedCpf.length === 11 && !loading;

	useEffect(() => {
		dispatch(fetchConsultaHistory());
	}, [dispatch]);

	const handleSearch = () => {
		if (canSearch) {
			// Reflete o CPF completo (com zeros) no campo antes de consultar.
			setCpf(maskCpf(paddedCpf));
			dispatch(consultarCpf(paddedCpf));
		}
	};

	const openLead = (cpfValue: string, available: boolean) => {
		if (loading) return;
		if (!available) {
			toastService.warning('Este lead expirou (mais de 7 dias). Faça uma nova consulta.');
			return;
		}
		dispatch(openHistoryLead(cpfValue));
	};

	return (
		<Box pt={{ base: '20px', md: '10px' }}>
			<Card>
				<Text fontSize='lg' fontWeight='700' color={textColor} mb='4px'>
					Consultar CPF
				</Text>
				<Text color={subColor} fontSize='sm' mb='16px'>
					Higienize e enriqueça um lead informando o CPF.
				</Text>
				<Flex gap='12px' direction={{ base: 'column', sm: 'row' }}>
					<InputGroup flex='1'>
						<InputLeftElement pointerEvents='none' h='100%'>
							<Icon as={MdSearch} color='secondaryGray.600' />
						</InputLeftElement>
						<Input
							as={InputMask as any}
							mask='999.999.999-99'
							maskChar={null}
							variant='main'
							pl='40px'
							size='lg'
							placeholder='000.000.000-00'
							value={cpf}
							onChange={(e: any) => setCpf(e.target.value)}
							onKeyDown={(e: any) => {
								if (e.key === 'Enter') handleSearch();
							}}
						/>
					</InputGroup>
					<Button variant='brand' size='lg' px='40px' isDisabled={!canSearch} onClick={handleSearch}>
						Consultar
					</Button>
				</Flex>
			</Card>

			{history.length > 0 && (
				<Card mt='20px'>
					<Flex align='center' gap='8px' mb={historyOpen ? '12px' : '0'}>
						<Icon as={MdHistory} color='brand.500' />
						<Text fontWeight='700' color={textColor}>
							Últimos leads pesquisados
						</Text>
						<Badge borderRadius='8px' bg='brand.50' color='brand.600' fontSize='10px'>
							7 dias
						</Badge>
						<IconButton
							aria-label={historyOpen ? 'Minimizar histórico' : 'Expandir histórico'}
							icon={<Icon as={historyOpen ? MdExpandLess : MdExpandMore} w='22px' h='22px' />}
							size='sm'
							variant='ghost'
							color='secondaryGray.600'
							ml='auto'
							onClick={() => setHistoryOpen(o => !o)}
						/>
					</Flex>
					<Collapse in={historyOpen} animateOpacity>
					<Box>
						{history.map(item => (
							<Flex
								key={item.cpf}
								align='center'
								justify='space-between'
								py='10px'
								px='10px'
								borderRadius='10px'
								cursor={item.available ? 'pointer' : 'not-allowed'}
								opacity={item.available ? 1 : 0.55}
								_hover={{ bg: item.available ? rowHover : 'transparent' }}
								transition='.15s all ease'
								onClick={() => openLead(item.cpf, item.available)}>
								<Box>
									<Text fontWeight='600' color={textColor} fontSize='sm'>
										{item.name || 'Lead sem nome'}
									</Text>
									<Text fontSize='xs' color={subColor}>
										{maskCpf(item.cpf)} · {format(new Date(item.searchedAt), 'dd/MM/yyyy HH:mm')}
									</Text>
								</Box>
								<Flex align='center' gap='8px'>
									{item.available ? (
										<Badge colorScheme='green' borderRadius='8px' fontSize='10px'>
											Grátis
										</Badge>
									) : (
										<Badge colorScheme='gray' borderRadius='8px' fontSize='10px'>
											Expirado
										</Badge>
									)}
									<Icon as={MdChevronRight} color='secondaryGray.600' />
								</Flex>
							</Flex>
						))}
					</Box>
					</Collapse>
				</Card>
			)}

			{loading && (
				<Center py='60px'>
					<Spinner size='xl' color='brand.500' thickness='4px' />
				</Center>
			)}

			{!loading && !searched && history.length === 0 && (
				<Center py='80px' flexDirection='column'>
					<Icon as={TbDatabaseSearch} w='60px' h='60px' color='secondaryGray.600' mb='16px' />
					<Text color={subColor} fontWeight='600'>
						Informe um CPF para começar a consulta.
					</Text>
				</Center>
			)}

			{!loading && searched && result && result.status === QueryStatus.SUCCESS && <ResultCard result={result} />}

			{!loading && searched && result && result.status === QueryStatus.NOT_FOUND && (
				<Center py='80px' flexDirection='column'>
					<Icon as={MdSearchOff} w='60px' h='60px' color='orange.500' mb='16px' />
					<Text color={textColor} fontWeight='700'>
						CPF não encontrado
					</Text>
					<Text color={subColor} fontSize='sm'>
						Não localizamos dados para este CPF.
					</Text>
				</Center>
			)}

			{!loading && searched && (!result || result.status === QueryStatus.ERROR) && (
				<Center py='80px' flexDirection='column'>
					<Icon as={MdErrorOutline} w='60px' h='60px' color='red.500' mb='16px' />
					<Text color={textColor} fontWeight='700'>
						Erro na consulta
					</Text>
					<Text color={subColor} fontSize='sm'>
						Tente novamente em instantes.
					</Text>
				</Center>
			)}
		</Box>
	);
}
